import { ENDPOINT } from '~/constants';

import { type UUID } from '~/types/common';

import { HTTPError, vestigasApi } from '~/services/kyClient';
import ToastService from '~/services/toast.service';

import { i18n, log } from '~/utils';

import { zodCompanyAliasAddressSchema } from '../validations';

import {
  type UpdateCompanyAliasAddressPayload,
  type UpdateCompanyAliasAddressResponse,
} from './types';
import {
  zodUpdateCompanyAliasAddressEndpointSchema,
  zodUpdateCompanyAliasAddressPayloadSchema,
} from './validations';

/**
 * Updates a company alias address.
 *
 * @param aliasAddressId - The ID of the company alias address to update.
 * @param companyAliasAddress - The payload containing the updated company alias address data.
 * @returns - A promise that resolves with the API response.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Company-Alias-Addresses/operation/update_company_address_vadmin_company_alias_address__company_address_uuid__put
 */
export const updateCompanyAliasAddress = async ({
  aliasAddressId,
  companyAliasAddress,
}: {
  aliasAddressId: UUID;
  companyAliasAddress: UpdateCompanyAliasAddressPayload;
}) => {
  try {
    return await vestigasApi
      .put(ENDPOINT.COMPANY.ALIAS_ADDRESS.UPDATE(aliasAddressId), {
        json: companyAliasAddress,
        validate: {
          body: zodUpdateCompanyAliasAddressPayloadSchema,
          pathSegments: zodUpdateCompanyAliasAddressEndpointSchema,
          response: zodCompanyAliasAddressSchema,
        },
      })
      .json<UpdateCompanyAliasAddressResponse>();
  } catch (error) {
    log.error({
      message: 'Error updating company alias address',
      error,
    });

    let errorMessage = '';

    if (error instanceof HTTPError) {
      switch (error.response.status) {
        case 422: {
          errorMessage = i18n.t(
            ($) => $.company.updateCompanyAliasAddress.error422,
            {
              ns: 'data',
            },
          );
          break;
        }

        default: {
          errorMessage = i18n.t(
            ($) => $.company.updateCompanyAliasAddress.error.generic,
            { ns: 'data' },
          );
        }
      }
    }

    if (errorMessage) {
      ToastService.error(errorMessage);
    }

    // Re-throw the error to propagate it to the calling function.
    throw error;
  }
};
